import React from 'react';
import { RefreshCw, Loader2 } from 'lucide-react';
import { CustomerIntelligenceData, getCourierIntelligence, isValidCustomerPhone, normalizeCustomerPhone } from './customerIntelligence';
export type { CustomerIntelligenceData } from './customerIntelligence';

interface CustomerIntelligenceWidgetProps {
  customerHistory: CustomerIntelligenceData | null;
  loading: boolean;
  error?: string;
  phone?: string;
  onRefresh?: () => void;
  currentOrderNumber?: string;
  showStoreOrdersList?: boolean;
}

export const CustomerIntelligenceWidget: React.FC<CustomerIntelligenceWidgetProps> = ({
  customerHistory,
  loading,
  error = '',
  phone = '',
  onRefresh,
  currentOrderNumber,
  showStoreOrdersList = true,
}) => {
  const cleanPhone = normalizeCustomerPhone(phone);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {/* Top Header Row with Refresh button */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <span style={{ fontSize: '0.9rem', fontWeight: '700', color: '#1e293b' }}>
            Customer Delivery Intelligence
          </span>
          <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#64748b' }}>
            Cross-merchant delivery success analysis across Steadfast and Pathao networks
          </p>
        </div>
        {onRefresh && (
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading || !isValidCustomerPhone(phone)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '7px 14px',
              backgroundColor: '#f1f5f9',
              color: '#334155',
              border: '1px solid #cbd5e1',
              borderRadius: '7px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: loading || !cleanPhone ? 'not-allowed' : 'pointer',
              opacity: loading || !cleanPhone ? 0.7 : 1,
            }}
          >
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh Intelligence
          </button>
        )}
      </div>

      {/* State 1: No phone entered */}
      {!cleanPhone ? (
        <div style={{ padding: '36px', textAlign: 'center', background: '#f8fafc', borderRadius: '10px', border: '1px dashed #cbd5e1', color: '#64748b' }}>
          <div style={{ fontSize: '1.8rem', marginBottom: '6px' }}>📞</div>
          <p style={{ fontSize: '0.9rem', fontWeight: '700', color: '#1e293b', margin: '0 0 4px' }}>Enter a Customer Phone Number</p>
          <p style={{ fontSize: '0.78rem', color: '#64748b', margin: 0 }}>Type an 11-digit phone number to automatically run fraud analysis.</p>
        </div>
      ) : !isValidCustomerPhone(phone) ? (
        <p>Enter a valid Bangladesh mobile number (01XXXXXXXXX).</p>
      ) : loading ? (
        /* State 2: Loading */
        <div style={{ padding: '40px', textAlign: 'center', color: '#2563eb' }}>
          <Loader2 size={24} className="animate-spin" style={{ margin: '0 auto 10px' }} />
          <p style={{ fontSize: '0.85rem', fontWeight: '600' }}>Querying Steadfast &amp; courier fraud database...</p>
        </div>
      ) : error ? (
        /* State 3: Error */
        <div style={{ padding: '12px 16px', background: '#fee2e2', color: '#b91c1c', borderRadius: '8px', fontSize: '12px', border: '1px solid #fecaca' }}>
          ⚠️ {error}
        </div>
      ) : !customerHistory ? (
        /* State 4: No records found */
        <div style={{ padding: '36px', textAlign: 'center', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0', color: '#64748b' }}>
          <p style={{ fontSize: '0.88rem', fontWeight: '600' }}>Courier history has not been checked for {phone}.</p>
          <p style={{ fontSize: '0.75rem', margin: '4px 0 0' }}>Use Refresh Intelligence to check delivery history.</p>
        </div>
      ) : (
        /* State 5: Active intelligence display */
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {/* Courier Cards */}
          {(['steadfast', 'pathao'] as const).map(courier => {
            const data = customerHistory.fraud_check?.[courier];
            if (!data && courier !== 'steadfast') return null;

            const courierLabel = courier === 'steadfast' ? 'Steadfast Courier Network' : 'Pathao Courier Network';
            const courierColor = courier === 'steadfast' ? '#6c3fbf' : '#e85d04';
            const intelligence = getCourierIntelligence(data);
            const isConfigError = intelligence.status === 'not_configured' || intelligence.status === 'inactive';

            if (isConfigError) {
              return (
                <div key={courier} style={{ border: '1px dashed #d1d5db', borderRadius: '8px', padding: '10px 14px', background: '#fafafa' }}>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: '#9ca3af', marginBottom: '2px' }}>
                    {courier === 'steadfast' ? '🚚' : '📦'} {courierLabel}
                  </div>
                  <div style={{ fontSize: '11px', color: '#64748b' }}>{intelligence.message}</div>
                </div>
              );
            }

            const statistics = intelligence.statistics;
            if (!statistics) {
              const isRateLimited = intelligence.status === 'rate_limited';
              return (
                <div key={courier} style={{
                  border: `1px solid ${isRateLimited ? '#fde68a' : '#fecaca'}`,
                  borderRadius: '8px',
                  padding: '10px 14px',
                  background: isRateLimited ? '#fffbeb' : '#fef2f2',
                }}>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: courierColor, marginBottom: '4px' }}>
                    {courier === 'steadfast' ? '🚚' : '📦'} {courierLabel}
                  </div>
                  <div style={{ fontSize: '11px', color: isRateLimited ? '#92400e' : '#b91c1c', lineHeight: '1.4' }}>
                    {isRateLimited ? '⏳' : '⚠️'} {intelligence.message}
                  </div>
                  {isRateLimited && (
                    <div style={{ fontSize: '10px', color: '#b45309', marginTop: '4px', fontStyle: 'italic' }}>
                      💡 Successful results are cached for up to six hours to reduce quota usage.
                    </div>
                  )}
                </div>
              );
            }

            const { total, delivered: deliveryCount, cancelled: cancelCount, returnRate: cancelRate, deliveryRate } = statistics;
            const isFraudRisk = statistics.isHighRisk;
            const isGood = statistics.isTrusted;
            const isNewCustomer = intelligence.status === 'no_history';

            return (
              <div
                key={courier}
                style={{
                  border: `1.5px solid ${isFraudRisk ? '#fecaca' : isGood ? '#bbf7d0' : '#e2e8f0'}`,
                  borderRadius: '10px',
                  background: isFraudRisk ? '#fff5f5' : isGood ? '#f0fdf4' : '#fff',
                  padding: '14px 18px',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.03)'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontWeight: '800', fontSize: '0.92rem', color: '#1e293b', textTransform: 'uppercase' }}>
                      {courier === 'steadfast' ? '🚚 ' : '📦 '} {courierLabel}
                    </span>
                    {isFraudRisk && (
                      <span style={{ fontSize: '0.7rem', fontWeight: '800', background: '#fee2e2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', border: '1px solid #fca5a5' }}>
                        ⚠️ HIGH RISK
                      </span>
                    )}
                    {isGood && (
                      <span style={{ fontSize: '0.7rem', fontWeight: '800', background: '#dcfce7', color: '#15803d', padding: '2px 8px', borderRadius: '10px', border: '1px solid #86efac' }}>
                        ✓ TRUSTED
                      </span>
                    )}
                    {isNewCustomer && (
                      <span style={{ fontSize: '0.7rem', fontWeight: '800', background: '#f3f4f6', color: '#6b7280', padding: '2px 8px', borderRadius: '10px', border: '1px solid #e5e7eb' }}>
                        🆕 NEW CUSTOMER
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: '0.8rem', fontWeight: '700', color: (deliveryRate ?? 0) >= 80 ? '#16a34a' : (deliveryRate ?? 0) >= 60 ? '#2563eb' : '#dc2626' }}>
                    {total > 0 ? `${deliveryRate}% Delivery Success` : '—'}
                  </span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', textAlign: 'center' }}>
                  <div style={{ background: '#fff', padding: '8px', borderRadius: '7px', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: '0.7rem', color: '#64748b' }}>Total Orders</div>
                    <div style={{ fontSize: '1.05rem', fontWeight: '800', color: '#1e293b' }}>{total}</div>
                  </div>
                  <div style={{ background: '#fff', padding: '8px', borderRadius: '7px', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: '0.7rem', color: '#16a34a' }}>Delivered</div>
                    <div style={{ fontSize: '1.05rem', fontWeight: '800', color: '#16a34a' }}>{deliveryCount}</div>
                  </div>
                  <div style={{ background: '#fff', padding: '8px', borderRadius: '7px', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: '0.7rem', color: '#dc2626' }}>Cancelled/Returned</div>
                    <div style={{ fontSize: '1.05rem', fontWeight: '800', color: '#dc2626' }}>{cancelCount}</div>
                  </div>
                  <div style={{ background: '#fff', padding: '8px', borderRadius: '7px', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: '0.7rem', color: '#d97706' }}>Return Rate</div>
                    <div style={{ fontSize: '1.05rem', fontWeight: '800', color: (cancelRate ?? 0) > 40 ? '#dc2626' : '#1e293b' }}>
                      {total > 0 ? `${cancelRate}%` : '—'}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Store History Summary */}
          {customerHistory.store_history && (
            <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px 18px', background: '#f8fafc' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>
                🏢 Previous Orders at Our Store
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', textAlign: 'center' }}>
                <div style={{ background: '#fff', padding: '8px', borderRadius: '7px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '0.7rem', color: '#64748b' }}>Store Orders</div>
                  <div style={{ fontSize: '1.05rem', fontWeight: '800', color: '#1e293b' }}>
                    {customerHistory.store_history.total_orders || 0}
                  </div>
                </div>
                <div style={{ background: '#fff', padding: '8px', borderRadius: '7px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '0.7rem', color: '#64748b' }}>Total Spent</div>
                  <div style={{ fontSize: '1.05rem', fontWeight: '800', color: '#2563eb' }}>
                    ৳{parseFloat(String(customerHistory.store_history.total_spent || 0)).toFixed(0)}
                  </div>
                </div>
                <div style={{ background: '#fff', padding: '8px', borderRadius: '7px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '0.7rem', color: '#64748b' }}>Cancelled Orders</div>
                  <div style={{ fontSize: '1.05rem', fontWeight: '800', color: '#dc2626' }}>
                    {customerHistory.store_history.cancelled_orders || 0}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Detailed Store Orders List (if present) */}
          {showStoreOrdersList && customerHistory.store_orders && customerHistory.store_orders.length > 0 && (
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
              <div style={{ padding: '8px 14px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '11px', fontWeight: '700', color: '#374151' }}>
                  🏪 Our Store Orders ({customerHistory.store_order_count || customerHistory.store_orders.length})
                </span>
              </div>
              <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                  <thead>
                    <tr style={{ background: '#f3f4f6', position: 'sticky', top: 0, zIndex: 1 }}>
                      <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Order</th>
                      <th style={{ padding: '6px 10px', textAlign: 'right', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Amount</th>
                      <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Status</th>
                      <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerHistory.store_orders.map((rec, idx) => {
                      const statusColors: Record<string, { bg: string; color: string; label: string }> = {
                        delivered: { bg: '#d1fae5', color: '#065f46', label: 'Delivered' },
                        shipped: { bg: '#dbeafe', color: '#1e40af', label: 'Shipped' },
                        processing: { bg: '#fef3c7', color: '#92400e', label: 'Processing' },
                        pending: { bg: '#f3f4f6', color: '#374151', label: 'Pending' },
                        cancelled: { bg: '#fee2e2', color: '#991b1b', label: 'Cancelled' },
                      };
                      const st = statusColors[rec.status || ''] || { bg: '#f3f4f6', color: '#374151', label: rec.status || 'Unknown' };
                      const isCurrentOrder = Boolean(currentOrderNumber && rec.order_number === currentOrderNumber);
                      return (
                        <tr key={idx} style={{
                          borderBottom: '1px solid #f3f4f6',
                          background: isCurrentOrder ? '#eff6ff' : idx % 2 === 0 ? '#fff' : '#fafafa',
                        }}>
                          <td style={{ padding: '6px 10px', fontWeight: isCurrentOrder ? '700' : '500', color: isCurrentOrder ? '#1d4ed8' : '#111827', fontSize: '11px' }}>
                            {rec.order_number || '—'}
                            {isCurrentOrder && <span style={{ fontSize: '8px', color: '#2563eb', marginLeft: '3px' }}>(this)</span>}
                          </td>
                          <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: '600', color: '#111827' }}>
                            ৳{parseFloat(String(rec.total_amount || 0)).toFixed(0)}
                          </td>
                          <td style={{ padding: '6px 10px' }}>
                            <span style={{ background: st.bg, color: st.color, padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '600' }}>
                              {st.label}
                            </span>
                          </td>
                          <td style={{ padding: '6px 10px', color: '#6b7280' }}>
                            {rec.created_at ? new Date(rec.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerIntelligenceWidget;
